import { Fragment, useEffect, useState } from "react";
import PageTitle from "../../layouts/PageTitle";
import axios from "axios";
import config from "../../../../config.json";
import CategoriesHistory from "./CategoriesHistory";
import swal from "sweetalert";
import "../../../i18n";
import { useTranslation } from "react-i18next";

const CategoryGridHistory = () => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState("");
  const [isError, setIsError] = useState(false);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${config.dev}/v2/categories/retrieve`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`, // Récupérer le token
        },
      });
      const data = response?.data || [];
      setCategories(data);
      setFilteredCategories(data);
      setIsError(false); // Réinitialise l'état d'erreur
    } catch (err) {
      // Définit un message d'erreur clair
      setError("Failed to fetch categories. Please try again later.");
      setIsError(true); // Définit l'état d'erreur à vrai
      console.error("Error fetching categories:", err);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    const results = categories.filter((product) => {
      // Vérification des détails du produit
      const matchesDetails = product.categoryDetails
        ? product.categoryDetails.some((detail) =>
            ["categoryTitle", "categoryDescription"].some((key) =>
              detail[key]?.toLowerCase().includes(searchTerm.toLowerCase())
            )
          )
        : false;

      return matchesDetails;
    });

    setFilteredCategories(results);
  }, [searchTerm, categories]);

  const archivedCategories = filteredCategories.filter((item) => item?.isArchived);
  const selectedCount = archivedCategories.filter((item) =>
    selectedIds.includes(item._id)
  ).length;
  const areAllSelected =
    archivedCategories.length > 0 &&
    archivedCategories.every((item) => selectedIds.includes(item._id));

  useEffect(() => {
    const validIds = categories.map((item) => item?._id).filter(Boolean);
    setSelectedIds((prev) => prev.filter((id) => validIds.includes(id)));
  }, [categories]);

  const toggleSelectOne = (id, checked) => {
    setSelectedIds((prev) => {
      if (checked) return prev.includes(id) ? prev : [...prev, id];
      return prev.filter((x) => x !== id);
    });
  };

  const toggleSelectAll = (checked) => {
    if (checked) {
      const ids = archivedCategories.map((item) => item._id);
      setSelectedIds((prev) => Array.from(new Set([...prev, ...ids])));
      return;
    }
    const ids = new Set(archivedCategories.map((item) => item._id));
    setSelectedIds((prev) => prev.filter((id) => !ids.has(id)));
  };

  const authHeaders = {
    headers: {
      Authorization: `Bearer ${localStorage.getItem("tokenLogin")}`,
    },
  };
  const restoreById = (id) =>
    axios.put(`${config.dev}/v2/categories/restore/${id}`, {}, authHeaders);
  const deleteById = (id) =>
    axios.delete(`${config.dev}/v2/categories/delete/${id}`, authHeaders);

  const handleRestoreSelected = async () => {
    const ids = archivedCategories
      .filter((item) => selectedIds.includes(item._id))
      .map((item) => item._id);
    if (ids.length === 0) return;
    const ok = await swal({ title: "Are you sure?", text: `Restore ${ids.length} selected categories?`, icon: "warning", buttons: true });
    if (!ok) return;
    await Promise.all(ids.map(restoreById));
    setSelectedIds([]);
    fetchCategories();
  };

  const handleDeleteSelected = async () => {
    const ids = archivedCategories
      .filter((item) => selectedIds.includes(item._id))
      .map((item) => item._id);
    if (ids.length === 0) return;
    const ok = await swal({ title: "Are you sure?", text: `Delete ${ids.length} selected categories forever?`, icon: "warning", buttons: true, dangerMode: true });
    if (!ok) return;
    await Promise.all(ids.map(deleteById));
    setSelectedIds([]);
    fetchCategories();
  };

  return (
    <Fragment>
      <PageTitle activeMenu={t("categories")} motherMenu={t("history")} />
      <div className="row">
        <div className="col-lg-6 col-md-12 col-sm-12">
          <input
            type="text"
            className="form-control input-rounded"
            placeholder={t("searchByTitle")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={filteredCategories?.length != null}
            disabled={!categories?.length}
          />
        </div>
      </div>
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div className="form-check m-0">
          <input
            type="checkbox"
            id="select-all-categories"
            className="form-check-input"
            checked={areAllSelected}
            onChange={(e) => toggleSelectAll(e.target.checked)}
            disabled={archivedCategories.length === 0}
          />
          <label className="form-check-label ms-2" htmlFor="select-all-categories">
            Select all ({archivedCategories.length})
          </label>
        </div>
        <div>
          <button className="btn btn-success btn-sm me-2" onClick={handleRestoreSelected} disabled={selectedCount === 0}>
            Restore Selected ({selectedCount})
          </button>
          <button className="btn btn-danger btn-sm" onClick={handleDeleteSelected} disabled={selectedCount === 0}>
            Delete Selected ({selectedCount})
          </button>
        </div>
      </div>
      <div className="row mt-5">
        {filteredCategories.length === 0 ? (
          <div className="col-12">
            {/* Message d'erreur affiché dans une alerte stylisée */}
            <div className="alert alert-warning" role="alert">
              {t("noDataFound")}
              <i className="fa fa-spinner" />
            </div>
          </div>
        ) : (
          // Affiche les produits sous forme de grille
          filteredCategories.map((category) => (
            <CategoriesHistory
              fetchCategories={fetchCategories}
              key={category._id}
              category={category}
              selected={selectedIds.includes(category._id)}
              onToggleSelect={toggleSelectOne}
            />
          ))
        )}
      </div>
    </Fragment>
  );
};

export default CategoryGridHistory;
